import $ from 'jquery';
import 'vue-template-compiler';
import { Vue, Component, Prop, Watch } from 'vue-property-decorator';
import Swiper from 'swiper';
import Readmore from 'readmore-js';
import { disableBodyScroll, enableBodyScroll, clearAllBodyScrollLocks } from 'body-scroll-lock';

import Section from '../Section';
import MediaWrapper from '../global/MediaWrapper';
import Accessibility from '../global/Accessibility';
import Dropdown from '../components/Dropdown.vue';
import { Breakpoints } from '../constants';

/** @typedef {import('theme-checker/dist/shopify').Variant} Variant */
/** @typedef {import('theme-checker/dist/shopify').Product} Product */
/** @typedef {import('theme-checker/dist/shopify').Media} Media */

const State = {
  ready: 'ready',
  soldOut: 'soldOut',
  added: 'added',
  unavailable: 'unavailable',
  adding: 'adding',
};

const MOBILE_BREAKPOINT = Breakpoints.xs;

@Component({
  components: {
    'wetheme-dropdown': Dropdown,
  },
})
class ProductForm extends Vue {

  /** @type {number | null} */
  @Prop({ required: true })
  initialVariantId;

  /** @type {import('theme-checker/dist/shopify').Product} */
  @Prop({ required: true })
  product;

  /** @type {import('../ThemeBase').default} */
  @Prop({ required: true })
  theme;

  /** @type {Product} */
  @Prop({ required: true })
  productSection;

  /** @type {boolean} */
  @Prop({ required: true })
  isFeaturedProduct;

  /** @type {boolean} */
  @Prop({ required: true })
  thumbnailChangesVariant;

  /** @type {boolean} */
  @Prop({ required: true })
  hasThumbnails;

  /** @type {boolean} */
  @Prop({ required: true })
  imageZoomEnabled;

  /** @type {boolean} */
  @Prop({ required: true })
  videoLoopEnabled;

  /** @type {string} */
  @Prop({ required: true })
  sectionId;

  /** @type {'drawer' | 'added' | 'page'} */
  @Prop({ required: true })
  cartMethod;

  /** @type {number} */
  @Prop({ required: true })
  slidesPerView;

  /** @type {number} */
  @Prop({ required: true })
  giftwrapProductVariantId;

  /** @type {boolean} */
  @Prop({ required: true })
  locationsEnabled;

  /** @type {string} */
  @Prop({required: false})
  languageUrl;

  /** @type {string} */
  option1 = '';
  /** @type {string | null} */
  option2 = null;
  /** @type {string | null} */
  option3 = null;
  /** @type {boolean} */
  addingToCart;
  /** @type {boolean} */
  afterAddToCart;
  /** @type {string} */
  mediaWrapperSpacing;
  /** @type {number} */
  activeMediaId;
  /** @type {Map<number, MediaWrapper>} */
  mediaWrapperById;
  /** @type {boolean} */
  showOutOfStockForm;
  /** @type {boolean} */
  isMobile;
  /** @type {number} */
  quantity = 1;
  /** @type {string} */
  hasErrors;
  /** @type {boolean} */
  giftwrapProduct;
  /** @type {boolean} */
  sendAsGift;

  /** @type {Swiper} */
  mediaSlider = null;
  /** @type {HTMLElement} */
  locationsModal = null;
  /** @type {Element} */
  modalLink = null;
  /** @type {Accessibility} */
  Accessibility;

  data() {
    const variant = this.product.variants.find(v => v.id === this.initialVariantId);
    let media;
    if (variant.featured_media) {
      media = variant.featured_media;
    } else if (this.product.media && this.product.media.length > 0) {
      media = this.product.media[0];
    }
    return {
      option1: variant.option1,
      option2: variant.option2,
      option3: variant.option3,
      addingToCart: false,
      afterAddToCart: false,
      mediaWrapperSpacing: '',
      activeMediaId: media ? media.id : undefined,
      mediaWrapperById: new Map(),
      showOutOfStockForm: false,
      isMobile: window.matchMedia(`only screen and (max-width: ${MOBILE_BREAKPOINT.max}px)`).matches,
      quantity: 1,
      hasErrors: '',
      giftwrapProduct: false,
      sendAsGift: false,
      hasLocalPickup: '',
      locationsModal: null,
      modalLink: null,
      Accessibility: null
    };
  }

  /** @returns {Variant | null} */
  get variant() {
    return this.product.variants.find(v => v.option1 === this.option1 && v.option2 == this.option2 && v.option3 === this.option3);
  }

  /** @returns {boolean} */
  get isSale() {
    return this.variant && this.variant.compare_at_price && this.variant.compare_at_price > this.variant.price;
  }

  /** @return {State[keyof State]} */
  get state() {
    if (this.addingToCart) {
      return State.adding;
    }
    if (this.afterAddToCart) {
      return State.added;
    }
    if (this.variant) {
      if (this.variant.available) {
        return State.ready;
      } else {
        return State.soldOut;
      }
    } else {
      return State.unavailable;
    }
  }

  /** @returns {boolean} */
  get addToCartDisabled() {
    return !this.variant || !this.variant.available;
  }

  /** @returns {Media | undefined} */
  get activeMedia() {
    return (this.product.media || []).find(m => m.id === this.activeMediaId);
  }

  /** @returns {Media | undefined} */
  get firstOrActiveModel() {
    if (this.activeMedia && this.activeMedia.media_type === 'model') {
      return this.activeMedia;
    }
    return (this.product.media || []).find(m => m.media_type === 'model');
  }

  increaseQuantity() {
    this.quantity = +this.quantity + 1;
  }

  decreaseQuantity() {
    this.quantity = Math.max(1, +this.quantity - 1);
  }

  /** @param {number} mediaId */
  getMedia(mediaId) {
    let media = this.mediaWrapperById.get(mediaId);
    if (!media) {
      const m = (this.product.media || []).find(m => m.id === mediaId);
      const target = this.$refs.mediaTargets[m.position - 1];
      media = new MediaWrapper(this.theme, this.sectionId, m, target);
      target.addEventListener('media-play', this.onMediaPlay.bind(this));
      target.addEventListener('media-pause', this.onMediaPause.bind(this));
      target.addEventListener('media-load', this.onMediaLoaded.bind(this));
      this.mediaWrapperById.set(mediaId, media);
    }
    return media;
  }

  async updateStoreAvailabilityContent(variantId, productTitle) {
    if (!this.locationsEnabled) return;

    const initialContent = this.$el.querySelector('#initialPickupInfo');
    const variantSectionUrl =
      '/variants/' + variantId + '/?section_id=store-availability';
    const response = await window.fetch(variantSectionUrl);
    const responseText = await response.text();

    let tempDomNode = document.createElement('div');
    tempDomNode.innerHTML = responseText;

    const hasPickupContent = tempDomNode.querySelector('.store-availability-information');
    const pickupHTML = tempDomNode.firstElementChild.innerHTML;

    if (hasPickupContent && initialContent.innerHTML != pickupHTML) {
      // @ts-ignore
      initialContent.style.display = 'none';
      this.hasLocalPickup = pickupHTML;
    } else if (!hasPickupContent) {
      this.hasLocalPickup = '';
    }

    this.$nextTick(() => {
      // Write product full title.
      let productTitleElem = this.$el.querySelector('.store-availabilities-modal__product-title');
      if (!productTitleElem) return;
      productTitleElem.textContent = productTitle;

      // Modal.
      this.locationsModal = this.$el.querySelector('.store-availabilities-modal');
      if (!this.locationsModal) return;

      // Event listeners.
      this.modalLink = this.$el.querySelector('.store-availability-information__link');
      if (this.modalLink) this.modalLink.addEventListener('click', this.openLocationsModal);

      this.modalClose = this.$el.querySelector('.store-availabilities-modal__close');
      if (this.modalClose) this.modalClose.addEventListener('click', this.closeLocationsModal);

      // Move modal html out of product info as that can be sticky, causing z-index problems.
      var parentNode = document.querySelector('#PageContainer');
      parentNode.insertBefore(this.locationsModal, parentNode.firstChild);
    });
  }

  handleLocationsClickOutside(e) {
    if (!e.target.classList.contains('store-availability-information__link') &&
      !this.locationsModal.contains(e.target)) {
      e.preventDefault();
      this.closeLocationsModal();
    }
  }

  openLocationsModal() {
    document.body.classList.add('js-drawer-location-modal-open');
    this.locationsModal.classList.add('locations-modal--is-active');
    disableBodyScroll(this.locationsModal);
    document.addEventListener('click', this.handleLocationsClickOutside);
    this.Accessibility.trapFocus({
      container: this.locationsModal,
      elementToFocus: this.$el.querySelector('.store-availability-information__link')
    });
  }

  closeLocationsModal() {
    document.body.classList.remove('js-drawer-location-modal-open');
    this.locationsModal.classList.remove('locations-modal--is-active');
    enableBodyScroll(this.locationsModal);
    document.removeEventListener('click', this.handleLocationsClickOutside);
    this.Accessibility.removeTrapFocus({
      container: this.locationsModal,
      elementToFocus: this.$el.querySelector('.store-availability-information__link')
    });
  }

  loadSlider() {
    this.mediaSlider = new Swiper(this.$el.querySelector('.swiper-container'), {
      direction: 'horizontal',
      slidesPerView: this.slidesPerView,
      centeredSlides: this.slidesPerView > 1,
      updateOnImagesReady: true,
      spaceBetween: 20,
      threshold: 10,
      autoHeight: true,
      pagination: {
        el: '.swiper-pagination',
        clickable: true,
      },
    });

    this.mediaSlider.on('slideChange', () => {
      if (this.thumbnailChangesVariant) {
        const media = this.product.media[this.mediaSlider.activeIndex];

        this.changeDropdownsToMedia(media);
      }
      this.mediaWrapperById.forEach(m => m.pause());
    });

    this.mediaSlider.allowTouchMove = this.isFeaturedProduct || this.isMobile;

    if (this.variant.featured_media) {
      this.slideTo(this.getMedia(this.variant.featured_media.id), true);
    }

    // Trigger the zoom on the first image
    if (!this.isFeaturedProduct) {
      let firstMedia = null;

      if (this.$el.querySelector('.product-medias__primary .product-medias__media')) {
        const firstId = parseInt(/** @type {HTMLElement} */(this.$el.querySelector('.product-medias__primary .product-medias__media')).dataset.mediaId, 10);
        firstMedia = this.getMedia(firstId);
      }

      if (firstMedia && this.imageZoomEnabled) {
        firstMedia.loadImageZoom();
      }
    }
  }

  unloadSlider() {
    this.mediaSlider.destroy();
    this.mediaSlider = undefined;
  }

  get hasSlider() {
    return this.isFeaturedProduct || this.isMobile;
  }

  get showThumbails() {
    return !this.isFeaturedProduct && this.hasThumbnails && !this.isMobile;
  }

  initApps() {
    let appBlocks = document.querySelectorAll('.shopify-app-block');

    if (appBlocks.length > 0) {
      appBlocks.forEach((element, index) => {
        let blockId = document.querySelector(`form #${element.id}`);

        if (blockId) {
          let appContent = document.querySelector(`.shopify-app-block#${blockId.id}`);
          let getContent = appContent.parentElement.removeChild(appContent);
          blockId.append(getContent);
        }
      });
    }
  }

  initBlock(blockSelector) {
    let targetBlock = document.querySelectorAll(blockSelector);

    if (targetBlock.length > 0) {
      targetBlock.forEach((element, index) => {
        let formEl = this.$el.querySelector('form');
        let blockId = formEl.querySelector(`#${element.id}`);

        if (blockId) {
          let blockContent = document.querySelector(`${blockSelector}#${blockId.id}`);
          let getContent = blockContent.parentElement.removeChild(blockContent);
          blockId.append(getContent);

          // Lets us know when the product blocks have loaded
          const event = new Event('product-blocks-ready');
          document.dispatchEvent(event);
        }
      });
    }
  }

  mounted() {
    this.initApps();
    this.initBlock('.complementary-products-block');
    this.initBlock('.cross-sell-block');

    let qtyNoscript = this.$el.querySelector('.quantity-noscript');
    let jsQtyNum = this.$el.querySelector('.js-qty__num');

    if (qtyNoscript) {
      qtyNoscript.setAttribute('name', 'no-js-quantity');
    }

    if (jsQtyNum) {
      jsQtyNum.setAttribute('name', 'quantity');
    }

    this.$el.querySelector('#AddToCartForm').addEventListener('submit', this.onAddToCart);
    this.mediaWrapperById = new Map();
    this.Accessibility = new Accessibility();

    if (this.showThumbails || this.hasSlider) {
      this.loadSlider();
    }

    this.updateStoreAvailabilityContent(this.variant.id, this.product.title);

    this.$el.addEventListener('product-media-select', this.onMediaSelect.bind(this));

    if (!this.hasThumbnails) {
      // Load zoom effect for full-size images when enabled
      const imgElems = /** @type {NodeListOf<HTMLElement>} */ (this.$el.querySelectorAll('.product-medias__fullsize .product-medias__media'));

      imgElems.forEach((imgElem) => {
        if (imgElem.dataset.mediaType == 'image') {
          const imgId = parseInt(imgElem.dataset.mediaId, 10);
          const imgMedia = this.getMedia(imgId);

          if (imgMedia && this.imageZoomEnabled) {
            imgMedia.loadImageZoom();
          }
        }
      });
    }

    if (this.firstOrActiveModel) {
      // We have at least one model => load XR library so
      // it can show/hide the button based on browser/device support
      window.Shopify.loadFeatures([
        {
          name: 'shopify-xr',
          version: '1.0',
        },
      ]);
      document.addEventListener('shopify_xr_launch', () => {
        // The launch of XR viewer triggers beforeload event, we need to reappear page content
        setTimeout(() => {
          document.body.classList.remove('unloading');
        }, 100);
      });
    }

    window.ShopifyXR = window.ShopifyXR || function () {
      (window.ShopifyXR.q = window.ShopifyXR.q || []).push(arguments);
    }
    const models = (this.product.media || []).filter(m => m.media_type === 'model');
    if (window.ShopifyXR.addModels) {
      window.ShopifyXR.addModels(models);
    } else {
      window.ShopifyXR('addModels', models);
    }

    this.giftCardRecipientsFields = /** @type {NodeListOf<HTMLInputElement>} */ (this.$el.querySelectorAll('[name*="Recipient"]'));
    this.giftCardRecipientsFieldsForm = /** @type {HTMLElement} */  (this.$el.querySelector('.gift-card--recipient-form-wrapper'));
  }

  @Watch('variant')
  onVariantChange() {
    if (this.variant) {
      if (!this.variant.available) {
        this.showOutOfStockForm = false;
        this.hidePaymentButtons();
      }
      else {
        this.showPaymentButtons();
      }

      this.setVariantUrl(this.variant);
      this.updateStoreAvailabilityContent(this.variant.id, this.product.title);
      const stockLevelIndicatorEl = this.$el.querySelector('stock-level-indicator');
      if (stockLevelIndicatorEl) this.updateStockLevelIndicator(stockLevelIndicatorEl, this.variant.id);

      if (this.variant.featured_media) {
        const mediaElement = this.$el.querySelector(`[data-media-id="${this.variant.featured_media.id}"]`);
        this.onMediaElementSelect(mediaElement, { load: true });
      }

      // Ensure the change event is fired on the form when variants changed (for Shop Pay Installments)
      const self = this;
      this.addToCartForm = this.$el.querySelector('#AddToCartForm');
      this.$nextTick(() => {
        const event = new Event('change');
        self.addToCartForm.dispatchEvent(event);
      });
    } else {
      this.hidePaymentButtons();
    }
  }

  onShowOutOfStockForm(e) {
    e.preventDefault();
    // When the HERE button is clicked, the target is whole text
    if (e.currentTarget.contains(e.target.closest('a'))) {
      this.showOutOfStockForm = true;
    }
  }

  onMediaPlay(e) {
    if (this.mediaSlider) {
      // Stop swiper swipe gestures, video is playing.
      this.mediaSlider.allowTouchMove = false;

      // There may be a difference between the poster image
      // and the video aspect ratio. Update height.
      this.mediaSlider.updateAutoHeight();
    }
  }

  onMediaPause(e) {
    if (this.mediaSlider) {
      // Restarts swiper swipe gestures, video isn't playing.
      this.mediaSlider.allowTouchMove = true;
    }
  }

  onMediaLoaded(e) {
    e.detail.parent.querySelector('.product-medias__media').addEventListener('click', this.onMediaActivate.bind(this));
  }

  onMediaSelect(e) {
    const element = /** @type {HTMLElement} */ ($(this.$el).find('.product-single__media')[e.detail.index]);
    const mediaWrapper = this.mediaWrapperById.get(parseInt(element.dataset.mediaId, 10));
    const isVideo = mediaWrapper.media.media_type === 'video' || mediaWrapper.media.media_type === 'external_video';
    const isQuickShop = this.$el.closest('#ShopNowContainer');
    this.onMediaElementSelect(element, {
      load: e.detail.forceLoad,
      forcePlay: e.detail.forcePlay || (!this.isMobile && !isQuickShop && isVideo), // Autoplay video on non-mobile and not in quick shop
      focus: e.detail.focus,
    });
  }

  /** @param {KeyboardEvent} e */
  onMediaActivate(e) {
    const target = /** @type {HTMLElement} */ (e.target);
    const currentTarget = /** @type {HTMLElement} */ (e.currentTarget);
    if (e.type === 'keydown') {
      if (e.keyCode !== 13 && e.keyCode !== 27) {
        return;
      }
      if (target.classList.contains('shopify-model-viewer-ui__button') || target.classList.contains('plyr__controls__item')) {
        return;
      }
    }
    e.preventDefault();

    if (target.classList.contains('product-medias__view-in-space')) {
      // Don't do anything on "view in your space" click, it's handled by Shopify XR library.
      return;
    }

    if (target.tagName === 'MODEL-VIEWER' || target.classList.contains('shopify-model-viewer-ui__controls-overlay')) {
      // Don't do anything on 3d model overlay click, it will trigger an event itself.
      return;
    }

    const isLoaded = currentTarget.classList.contains('product-single__media--loaded');
    const isActive = currentTarget.classList.contains('product-single__media--active')

    if (this.hasThumbnails) {
      this.onThumbnailClick(currentTarget);
    }

    if (isLoaded && isActive) {
      // Loaded and active media will handle click internally in the component itself.
      return;
    }

    this.onMediaElementSelect(currentTarget, { load: true, togglePlay: true });
  }

  onMediaElementSelect(element, options) {
    if (!element) {
      return;
    }

    const mediaWrapper = this.getMedia(parseInt(element.dataset.mediaId, 10));
    this.activeMediaId = mediaWrapper.media.id;

    this.slideTo(mediaWrapper);

    // Pause all others
    this.mediaWrapperById.forEach((otherMediaWrapper) => {
      if (otherMediaWrapper.mediaId !== mediaWrapper.mediaId) {
        otherMediaWrapper.pause();
      }
    });

    $(this.$el).find('.product-single__media--selected').not(element).removeClass('product-single__media--selected');

    element.classList.add('product-single__media--selected');

    if (options.togglePlay) {
      mediaWrapper.togglePlay();
    } else if (options.forcePlay) {
      mediaWrapper.play();
    } else if (options.load) {
      mediaWrapper.load();
    }
    if (options.focus) {
      mediaWrapper.focus();
    }
  }

  async onAddToCart(e) {
    e.preventDefault();
    this.addingToCart = true;
    this.hasErrors = '';

    try {
      const data = $(this.$el.querySelector('#AddToCartForm')).serialize();
      const $formContainer = $(this.$el.querySelector('#AddToCartForm'));

      let giftwrapProperty = '';

      if (this.giftwrapProduct && this.giftwrapProductVariantId) {
        giftwrapProperty = '&properties[' + this.theme.translations.giftwrap_label + ']=' + this.theme.translations.yes;
      }

      // Gift wrap add to cart.
      if (this.giftwrapProduct && this.giftwrapProductVariantId) {
        const giftData = {
          items: [
            {
              id: this.giftwrapProductVariantId,
              quantity: this.quantity
            }
          ]
        };

        const giftResponse = await window.fetch('/cart/add.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify(giftData),
        });

        const giftResponse1 = await giftResponse.json();
        if (!giftResponse.ok) {
          if (giftResponse1.status == 422) {
            this.hasErrors = giftResponse1.description;
          }
          this.addingToCart = false;
          return;
        }
      }

      // 'Actual' product add to cart.
      const response = await window.fetch('/cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: data + giftwrapProperty,
      });

      const response1 = await response.json();
      if (!response.ok) {
        // Handle various errors, quantity etc.
        // See: https://shopify.dev/docs/themes/ajax-api/reference/cart
        if (response1.status == 422) {
          this.hasErrors = response1.description;
        }
        this.addingToCart = false;
        return;
      }


      const languageParam = !this.languageUrl || this.languageUrl == '/' ? '' : this.languageUrl;
      const response2 = await window.fetch(`${languageParam}/cart?view=compare`);
      if (!response2.ok) {
        // FIXME: error handling
        this.addingToCart = false;
        return;
      }
      const cart = await response2.json();
      const featuredProductSubmit = this.$el.querySelector('[data-submit-type]');

      this.addingToCart = false;

      var cartBtn = /** @type {HTMLElement} */ (document.querySelector('#AddToCart'));
      var cartURL = cartBtn.dataset.cartUrl;

      if (this.cartMethod === 'drawer' && !featuredProductSubmit) {
        this.theme.toggleRightDrawer('cart', true, { cart: cart });
        this.giftProductFormOnAddToCart();
      } else if (this.cartMethod === 'added' && !featuredProductSubmit) {
        this.afterAddToCart = true;
        setTimeout(() => {
          this.afterAddToCart = false;
        }, 2000);
        this.theme.updateCartDrawer(cart);
        this.giftProductFormOnAddToCart();
      } else if (this.cartMethod === 'page' || featuredProductSubmit) {
        window.location.href = cartURL;
      }
    } catch (e) {
      console.error('Unable to add to cart: ', e);
      // FIXME error handling
    }

  }

  giftProductFormOnAddToCart() {
    if(!this.sendAsGift) return;
    this.emptyGiftPropductFormFields();
    this.toggleGiftCardRecipientFormVisibility(true);
    this.sendAsGift = false;
  }

  emptyGiftPropductFormFields() {
    this.giftCardRecipientsFields.forEach(input => input.value = '');
  }

  toggleGiftCardRecipientFormVisibility(visible) {
    this.giftCardRecipientsFields.forEach(input => visible ? input.disabled = true : input.disabled = false);
    this.giftCardRecipientsFieldsForm.style.display = visible ? 'none' : 'flex';
  }

  @Watch('sendAsGift', { deep: true })
  onSendAsGiftChange(newVal, oldVal) {
    this.toggleGiftCardRecipientFormVisibility(oldVal);
    this.sendAsGift = newVal;
  }

  onThumbnailClick(target) {
    const mediaWrapper = this.getMedia(parseInt(target.dataset.mediaId, 10));
    this.slideTo(mediaWrapper);

    this.changeDropdownsToMedia(mediaWrapper.media);
  }

  slideTo(mediaWrapper, skipAnimation = false) {
    if (this.mediaSlider) {
      this.mediaSlider.slideTo(mediaWrapper.media.position - 1, this.hasSlider && !skipAnimation ? 200 : 0);
    }
  }

  changeDropdownsToMedia(media) {
    // select variant
    if (this.thumbnailChangesVariant) {
      const variant = this.product.variants.find(v => v.featured_media && v.featured_media.id === media.id);
      if (variant) {
        this.option1 = variant.option1;
        this.option2 = variant.option2;
        this.option3 = variant.option3;
      }
    }
  }

  setVariantOption(value, name) {
    this[`option${name}`] = value;
  }

  /** @param {Variant} variant */
  setVariantUrl(variant) {
    const hasMoreVariants = this.product.variants.length > 1;
    if (history.replaceState && !this.isFeaturedProduct && hasMoreVariants) {
      // Change url to include variant if we're not on homepage
      let search = [];
      if (window.location.search.length > 1) {
        // select non-variant query params
        search = window.location.search.slice(1).split('&').filter((q) => {
          return q.indexOf('variant=') !== 0;
        });
      }
      // add current variant
      search.push('variant=' + variant.id);

      // set the url to include the variant
      const newurl = window.location.protocol + '//' + window.location.host + window.location.pathname + '?' + search.join('&');
      window.history.replaceState({ path: newurl }, '', newurl);
    }
  }

  updateStockLevelIndicator(stockLevelIndicator, variantId) {
    stockLevelIndicator.setAttribute('data-flow-variant-id', variantId);
  }

  hidePaymentButtons = () => {
    $('.shopify-payment-button').hide();
  };

  showPaymentButtons = () => {
    $('.shopify-payment-button').show();
  };

  /** @returns {string} */
  money(price) {
    return this.theme.formatMoney(price);
  }

  onWindowResizeBreakpoint({ breakpoint, oldBreakpoint }) {
    const newIsMobile = breakpoint.order <= MOBILE_BREAKPOINT.order;
    const oldWasMobile = oldBreakpoint.order <= MOBILE_BREAKPOINT.order;
    if (newIsMobile !== oldWasMobile) {
      this.isMobile = newIsMobile;

      if (this.mediaSlider) {
        this.mediaSlider.allowTouchMove = this.hasSlider;
      }

      if (!this.hasThumbnails) {
        if (this.hasSlider && !this.mediaSlider) {
          this.loadSlider();
        } else if (!this.hasSlider && this.mediaSlider) {
          this.unloadSlider();
        }
      }
    }
  }
}

export default class ProductSection extends Section {
  constructor(theme, element, pageLoad, options = {}) {
    super(theme, element, pageLoad);

    var product_json_el = this.element.querySelector('.product-json');
    if (!product_json_el) {
      // No product on the page - maybe just product placeholder
      return;
    }
    this.product = JSON.parse(product_json_el.textContent);

    this.vue = new Vue({
      el: this.element.querySelector('.product-form-vue'),
      components: {
        'wetheme-product-form': ProductForm,
      },
      data: () => ({
        productSection: this,
        product: this.product,
        theme: this.theme,
        isFeaturedProduct: this.isFeaturedProduct,
        slidesPerView: options ? options.slidesPerView || 1 : 1,
      }),
    });

    this.fixSticky();
    this.loadReadmore();
    this.loadTabs();
    this.scrollToPostedForm();

    document.addEventListener('shopify:block:select', this.loadReadmore);
    document.addEventListener('shopify:section:select', this.loadReadmore);
  }

  fixSticky = () => {
    const productSticky = /** @type {HTMLElement?} */ (this.element.querySelector('.product-sticky'));
    const headerSticky = document.querySelector('.sticky-header');

    if (productSticky && headerSticky) {
      productSticky.style.top = `${headerSticky.clientHeight}px`;
    }
  }

  get isFeaturedProduct() {
    return false;
  }

  onWindowResize = () => {
    this.fixSticky();
    this.loadReadmore();
  }

  onWindowResizeBreakpoint = ({ breakpoint, oldBreakpoint }) => {
    if (!this.vue) {
      // Product was not selected => no vue
      return;
    }
    (/** @type {ProductForm} */ (this.vue.$children[0])).onWindowResizeBreakpoint({ breakpoint, oldBreakpoint });
  }


  loadReadmore = () => {
    const readMoreEls = document.querySelectorAll('.product-description.readmore');
    if (!readMoreEls) return;

    let thisHeight = 100;
    if (this.element.querySelector('.product-description_full-width')) {
      thisHeight = 150;
      if (this.theme.getWindowWidth() <= 768) {
        thisHeight = 100;
      }
    }

    readMoreEls.forEach((readMoreEl) => {
      if (!readMoreEl.classList.contains('rmjs-1')) {
        this.readMore = new Readmore(readMoreEl, {
          speed: 100,
          collapsedHeight: thisHeight,
          moreLink: `<div class="read-more-link"><a href="#">
            ${this.theme.translations.read_more_html} 
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512" width="24" height="24" fill="currentColor"><!--! Font Awesome Pro 6.3.0 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license (Commercial License) Copyright 2023 Fonticons, Inc. --><path d="M206 365.3l22.6-22.6 160-160L411.3 160 366 114.7l-22.6 22.6L206 274.7 68.6 137.4 46 114.7 .7 160l22.6 22.6 160 160L206 365.3z"/></svg>
            </a></div>`,
          lessLink: `<div class="read-less-link"><a href="#">
            ${this.theme.translations.read_less_html} 
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512" width="24" height="24" fill="currentColor"><!--! Font Awesome Pro 6.3.0 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license (Commercial License) Copyright 2023 Fonticons, Inc. --><path d="M206 114.7l22.6 22.6 160 160L411.3 320 366 365.3l-22.6-22.6L206 205.3 68.6 342.6 46 365.3 .7 320l22.6-22.6 160-160L206 114.7z"/></svg>
            </a></div>`,
          beforeToggle: function (trigger, element, expanded) {
            if (expanded) {
              const headerEl = document.querySelector('.sticky-header');
              const stickyHeaderHeight = headerEl ? headerEl.clientHeight : 0;
              $('html, body').animate({
                scrollTop: $(element).offset().top - stickyHeaderHeight,
              }, 100);
            }
            $(element).toggleClass('closed');
          }
        });
      }
    });
  }

  loadTabs = () => {
    $(this.element).find('ul.tabs').each(function () {
      var active, content, links = $(this).find('a');
      active = links.first().addClass('active');
      content = $(active.attr('href'));
      links.not(':first').each(function () {
        $($(this).attr('href')).hide();
      });
      $(this).find('a').click(function (e) {
        active.removeClass('active');
        content.hide();
        active = $(this);
        content = $($(this).attr('href'));
        active.addClass('active');
        content.fadeIn();
        return false;
      });
    });
  }

  scrollToPostedForm = () => {
    if ($('.out-of-stock-form .posted-successfully').length) {
      $('html, body').animate({
        scrollTop: $('.form-vertical').offset().top,
      }, 100);
    }
  }
}
